import { useMemo, useState } from 'react'
import { ExternalLink, GitPullRequest, Github, Hash, MessageSquare, Paperclip, Send, Settings2, StickyNote, User } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { MentionTextarea } from '@/components/composites/mention-textarea'
import { AttachmentsField, cleanAttachments, type AttachmentRef } from '@/components/composites/attachments-field'
import type { AvatarPerson } from '@/components/composites/user-avatar'
import { LoadingState } from '@/components/composites/states'
import { getErrorMessage } from '@/api/errors'
import { cn } from '@/lib/utils'
import { activityFileUrl, priorityName, useDeskAction, useDeskActivity, usePostActivity, type ActivityFile, type ActivityItem, type DeskEvent, type SupportCase } from './api'
import { useMembersById } from './parts'

/**
 * One activity feed per ticket: what the team said (Tapwork messages, the ticket's Slack thread,
 * GitHub), what the customer said (Intercom), and what happened (the workflow events), in one
 * list with filters. Messages are internal by default; switching to "Message the customer" sends
 * a customer-visible remark through Intercom instead.
 */

export type FeedFilter = 'all' | 'team' | 'customer' | 'system'
const FILTERS: Array<{ id: FeedFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'team', label: 'Team' },
  { id: 'customer', label: 'Customer' },
  { id: 'system', label: 'System' },
]

export const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

export const EVENT_WORD: Record<string, string> = {
  created: 'Ticket opened',
  triaged: 'Triaged',
  routed: 'Sent to engineering',
  picked_up: 'Picked up',
  asked: 'Asked the customer',
  customer_replied: 'Customer replied — clock running again',
  severity: 'Priority changed',
  restored: 'Restored',
  fixed: 'Fixed',
  reassigned: 'Reassigned',
  closed: 'Closed',
  reopened: 'Reopened',
  dismissed: 'Dismissed',
  note: 'Remark',
  suggested: 'AI suggestion',
  linked: 'Updated',
  customer_message: 'Message sent to the customer',
  customer_update: 'Customer updated',
  escalated: 'Escalated',
}
const PRIORITY_KINDS = new Set(['severity', 'triaged'])
const CUSTOMER_EVENTS = new Set(['asked', 'customer_message', 'customer_replied', 'customer_update'])

type Entry =
  | { type: 'activity'; key: string; at: string; filter: Exclude<FeedFilter, 'all'>; item: ActivityItem }
  | { type: 'event'; key: string; at: string; filter: Exclude<FeedFilter, 'all'>; event: DeskEvent }

export function feedEntries(items: ActivityItem[], events: DeskEvent[]): Entry[] {
  const out: Entry[] = [
    ...items.map((item) => ({ type: 'activity' as const, key: `a:${item.id}`, at: item.at, filter: item.kind === 'intercom' ? ('customer' as const) : ('team' as const), item })),
    ...events.map((event) => ({
      type: 'event' as const,
      key: `e:${event.id}`,
      at: event.at,
      filter: event.customerVisible || CUSTOMER_EVENTS.has(event.kind) ? ('customer' as const) : event.kind === 'note' ? ('team' as const) : ('system' as const),
      event,
    })),
  ]
  return out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
}

const SOURCE: Record<ActivityItem['kind'], { label: string; Icon: typeof MessageSquare }> = {
  message: { label: 'Tapwork', Icon: MessageSquare },
  slack: { label: 'Slack thread', Icon: Hash },
  intercom: { label: 'Intercom', Icon: User },
  intercom_note: { label: 'Intercom note', Icon: StickyNote },
  github_comment: { label: 'GitHub', Icon: Github },
  github_pr: { label: 'Pull request', Icon: GitPullRequest },
  github_issue: { label: 'GitHub', Icon: Github },
  resolution: { label: 'Resolution summary', Icon: Settings2 },
}

function FileLink({ caseId, activityId, file }: { caseId: string; activityId: string; file: ActivityFile }) {
  if (file.url) {
    return (
      <a className="inline-flex items-center gap-1 text-xs text-brand underline" href={file.url} target="_blank" rel="noreferrer">
        <Paperclip className="size-3" />{file.name}
      </a>
    )
  }
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-xs text-brand underline"
      onClick={() => {
        // Open the tab first, so the browser does not treat it as a popup once the link arrives.
        const tab = window.open('', '_blank')
        activityFileUrl(caseId, activityId, file.id, 'inline')
          .then((url) => (tab ? (tab.location.href = url) : window.open(url, '_blank')))
          .catch((err) => {
            tab?.close()
            toast.error(getErrorMessage(err))
          })
      }}
    >
      <Paperclip className="size-3" />{file.name}
    </button>
  )
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const { label, Icon } = SOURCE[item.kind]
  const customer = item.authorKind === 'customer'
  return (
    <li className={cn('rounded-lg border px-3 py-2', customer ? 'border-border bg-card' : item.kind === 'intercom' ? 'border-brand/20 bg-brand-softer' : 'border-border-soft')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold">
          <Icon className="size-3.5 text-text-muted" />
          {item.authorName ?? label}
          <span className="font-normal text-text-muted">{customer ? 'Customer' : label}{item.kind === 'intercom' && !customer ? ' · to the customer' : ''}</span>
        </p>
        <p className="inline-flex items-center gap-2 text-tiny text-text-muted">
          {when(item.at)}
          {item.link ? <a href={item.link} target="_blank" rel="noreferrer" aria-label="Open where it was said"><ExternalLink className="size-3" /></a> : null}
        </p>
      </div>
      {item.resolution ? (
        <dl className="mt-1 grid gap-1 text-sm">
          {(['problem', 'fix', 'cause', 'prevention'] as const).filter((k) => item.resolution![k]?.trim()).map((k) => (
            <div key={k}>
              <dt className="text-xs font-semibold">{RESOLUTION_LABEL[k]}</dt>
              <dd className="whitespace-pre-wrap text-text-secondary">{item.resolution![k]}</dd>
            </div>
          ))}
        </dl>
      ) : item.body ? (
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text-secondary">{item.body}</p>
      ) : null}
      {item.files.length ? (
        <ul className="mt-1 flex flex-wrap gap-2">
          {item.files.map((f) => <li key={f.id}><FileLink caseId={item.caseId} activityId={item.id} file={f} /></li>)}
        </ul>
      ) : null}
    </li>
  )
}

export const RESOLUTION_LABEL = { problem: 'What was wrong', fix: 'What was done', cause: 'Cause', prevention: 'Prevention' } as const

function EventRow({ e }: { e: DeskEvent }) {
  return (
    <li className="rounded-md border border-dashed border-border-soft px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">
          {EVENT_WORD[e.kind] ?? e.kind}
          {e.from || e.to ? (
            <span className="font-normal text-text-secondary">
              {' '}
              {e.from ? `${PRIORITY_KINDS.has(e.kind) ? priorityName(e.from) : e.from} → ` : ''}
              {e.to ? (PRIORITY_KINDS.has(e.kind) ? priorityName(e.to) : e.to) : ''}
            </span>
          ) : null}
        </p>
        <p className="text-tiny text-text-muted">{e.byName} · {when(e.at)}</p>
      </div>
      {e.text ? <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{e.text}</p> : null}
      {e.eta ? <p className="mt-1 text-xs text-text-secondary"><strong>Recovery ETA:</strong> {when(e.eta)}</p> : null}
      {e.evidence ? <p className="mt-1 whitespace-pre-wrap text-xs text-text-secondary"><strong>Test evidence:</strong> {e.evidence}</p> : null}
      {e.customerVisible ? <p className="mt-1 text-tiny text-text-muted">{e.sent ? 'Sent to the customer in Intercom' : 'Customer-facing — send it in Intercom (auto-replies are off)'}</p> : null}
    </li>
  )
}

function Composer({ c }: { c: SupportCase }) {
  const members = useMembersById()
  const post = usePostActivity()
  const run = useDeskAction()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<AttachmentRef[]>([])
  const [uploading, setUploading] = useState(false)
  const [toCustomer, setToCustomer] = useState(false)
  const [round, setRound] = useState(0)
  const people = useMemo<AvatarPerson[]>(() => members.list.map((m) => ({ id: m.id, displayName: m.displayName, photoUrl: m.photoUrl ?? null })), [members.list])
  const busy = post.isPending || run.isPending || uploading
  const ready = toCustomer ? Boolean(text.trim()) : Boolean(text.trim() || files.length)
  const reset = () => {
    setText('')
    setFiles([])
    setToCustomer(false)
    setRound((r) => r + 1)
  }
  const send = () => {
    if (!ready || busy) return
    if (toCustomer) run.mutate({ id: c.id, action: 'note', body: { text, customerVisible: true } }, { onSuccess: reset })
    else post.mutate({ id: c.id, text: text.trim(), attachments: cleanAttachments(files) }, { onSuccess: reset })
  }
  return (
    <div className={cn('grid gap-2 rounded-lg border p-3', toCustomer ? 'border-warning/50 bg-warning-soft' : 'border-border bg-muted/20')}>
      <MentionTextarea
        value={text}
        onChange={setText}
        people={people}
        bracketMentions
        onSubmit={send}
        rows={3}
        maxLength={toCustomer ? 4000 : 8000}
        placeholder={toCustomer ? 'Message to the customer — sent through Intercom' : 'Internal message to the team…  @[name] to mention, ⌘↵ to send'}
      />
      {!toCustomer ? <AttachmentsField key={round} label="Screenshots, logs or PDFs" onChange={setFiles} onBusyChange={setUploading} listenForPaste={false} /> : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <Switch checked={toCustomer} onCheckedChange={setToCustomer} /> Message the customer
        </label>
        <Button size="sm" disabled={!ready || busy} onClick={send}>
          <Send className="size-3.5" /> {toCustomer ? 'Send to customer' : 'Send'}
        </Button>
      </div>
      <p className="text-tiny text-text-muted">
        {toCustomer
          ? 'Sent in Intercom when customer auto-replies are on; otherwise it is recorded here for you to send there.'
          : 'Internal only. Anyone you @mention is told in Tapwork and Slack.'}
      </p>
    </div>
  )
}

export function ActivityFeed({ c, mayAct }: { c: SupportCase; mayAct: boolean }) {
  const feed = useDeskActivity(c.id)
  const [filter, setFilter] = useState<FeedFilter>('all')
  const entries = useMemo(() => feedEntries(feed.data?.items ?? [], c.events), [feed.data, c.events])
  const shown = filter === 'all' ? entries : entries.filter((e) => e.filter === filter)
  const capture = feed.data?.capture
  return (
    <section className="grid content-start gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Activity</h3>
        <div role="tablist" aria-label="Show" className="flex gap-1 rounded-md bg-muted p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={cn('rounded px-2.5 py-1 text-xs font-medium', filter === f.id ? 'bg-card text-foreground shadow-e1' : 'text-text-secondary hover:text-foreground')}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {mayAct ? <Composer c={c} /> : null}
      {capture && (capture.intercom === 'failed' || capture.github === 'failed') ? (
        <p className="text-tiny text-warning">
          {capture.intercom === 'failed' ? 'Intercom could not be reached; showing the last copy kept here. ' : ''}
          {capture.github === 'failed' ? 'GitHub could not be reached; comments may be behind.' : ''}
        </p>
      ) : null}
      {feed.isPending ? (
        <LoadingState label="Reading the conversation…" />
      ) : !shown.length ? (
        <p className="text-sm text-text-muted">Nothing here yet.</p>
      ) : (
        <ol className="grid gap-2">
          {shown.map((e) => (e.type === 'activity' ? <ActivityRow key={e.key} item={e.item} /> : <EventRow key={e.key} e={e.event} />))}
        </ol>
      )}
    </section>
  )
}
